import { IUIError, UIErrorHandlingService } from "@gtmhub/error-handling";
import {
  IGtmhubUserToWorkdayWorkerMappings,
  IOrganization,
  IOrganizationTeamMapping,
  IWorkdayWorker,
  WorkdayMappingRunStatus,
} from "@gtmhub/configuration/workday/models";
import { WorkdayUserProvisioningService } from "@gtmhub/configuration/workday/workday-user-provisioning-service";
import { StateService } from "@uirouter/angularjs";
import { IComponentOptions, IIntervalService, IPromise } from "angular";
import { toCsv } from "@gtmhub/shared/utils";
import { INgRedux } from "@gtmhub/state-management";
import { ITeamsIdMap, ITeamsStoreState, TeamsActions } from "@gtmhub/teams";
import { OrganizationsActions } from "@gtmhub/configuration/workday/redux/organizations-actions";
import { IUser, UserService } from "@gtmhub/users";
import { IOrganizationsStoreState } from "@gtmhub/configuration/workday/redux/organizations-reducer";
import { Unsubscribe } from "redux";
import { IExtendedRootScopeService } from "@gtmhub/shared/ng-extensions";
import { triggerFileDownload } from "@gtmhub/shared/dom";

const pollingInterval = 2000; // 2 sec

export class WizardMappingCtrl {
  indicators: {
    loading?: boolean;
    isMappingRunning?: boolean;
    mappingFailed?: boolean;
  };
  intervalValue: IPromise<unknown>;
  currentMappingId: string;
  teamsFetched: boolean;
  organizationsFetched: boolean;
  teamsReduxError: IUIError;
  organizationsReduxError: IUIError;
  organizationTeamMappings: IOrganizationTeamMapping[] = [];
  organizations: IOrganization[] = [];
  teams: ITeamsIdMap;
  gtmhubUserToWorkdayWorkerMappings: IGtmhubUserToWorkdayWorkerMappings[] = [];
  mappedUsersNo: number;
  mappedOrganizationsNo: number;
  teamsNotMappedNo: number;
  doesOrgTeamMappingsExist: boolean;

  private unsubscribeFromStore: Unsubscribe;
  static $inject = [
    "$scope",
    "WorkdayUserProvisioningService",
    "UIErrorHandlingService",
    "$state",
    "$ngRedux",
    "TeamsActions",
    "OrganizationsActions",
    "UserService",
    "$interval",
  ];

  constructor(
    private $scope: IExtendedRootScopeService,
    private workdayUserProvisioningService: WorkdayUserProvisioningService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private $state: StateService,
    private $ngRedux: INgRedux,
    private teamsActions: TeamsActions,
    private organizationsActions: OrganizationsActions,
    private userService: UserService,
    private $interval: IIntervalService
  ) {}

  $onInit(): void {
    this.indicators = {
      isMappingRunning: false,
      loading: false,
    };

    const bindStateToScope = (state: ITeamsStoreState & IOrganizationsStoreState) => {
      return {
        organizations: state.organizations.organizations,
        organizationsFetched: state.organizations.isFetched,
        organizationsReduxError: state.organizations.error,
        teams: state.teams.map,
        teamsFetched: state.teams.isFetched,
        teamsReduxError: state.teams.error,
      };
    };
    this.indicators.loading = true;
    this.unsubscribeFromStore = this.$ngRedux.connect(bindStateToScope.bind(this.$state))(this);
    this.$ngRedux.dispatch(this.teamsActions.fetchTeamsIfMissing());
    this.$ngRedux.dispatch(this.organizationsActions.fetchOrganizationsIfMissing());
    this.$scope
      .watchUntilAndRejectOnError(
        () => this.organizationsFetched && this.teamsFetched,
        () => this.teamsReduxError || this.organizationsReduxError
      )
      .then(() => {
        this.getCurrentMappingIDAndCheckIfMappingIsRunning();
      })
      .catch((error) => {
        this.uiErrorHandlingService.handleModal(error);
        this.indicators.loading = false;
      });

    this.$scope.$on("mappingUpdated", () => this.updateMapping());
  }

  private updateMapping(): void {
    this.organizationTeamMappings = [];
    this.workdayUserProvisioningService.mapTeamsToOrganizationTeamMappings(this.teams, this.organizationTeamMappings);
    this.getOrganizationTeamMappings();
  }

  $onDestroy(): void {
    this.unsubscribeFromStore();
    this.clearIntervalValue();
  }

  loadingInProgress(): boolean {
    return this.indicators.loading || this.indicators.isMappingRunning;
  }

  showNoTeamsScreen(): boolean {
    return !this.loadingInProgress() && (!this.teams || Object.keys(this.teams).length === 0) && !this.indicators.mappingFailed;
  }

  showMappingFailedScreen(): boolean {
    return !this.loadingInProgress() && this.indicators.mappingFailed;
  }

  showMappingScreen(): boolean {
    return !this.loadingInProgress() && !this.showMappingFailedScreen() && !this.showNoTeamsScreen();
  }

  backToSelections() {
    this.$scope.$root.$broadcast("backClicked");
  }

  getCurrentMappingIDAndCheckIfMappingIsRunning(): void {
    this.indicators.isMappingRunning = true;
    this.workdayUserProvisioningService
      .getWorkdayUserProvisionSettings()
      .then((workdaySettings) => {
        this.currentMappingId = workdaySettings.unfinishedSetupMappingId;
        if (this.currentMappingId) {
          this.checkIfMappingIsDone();
        } else {
          this.workdayUserProvisioningService.mapTeamsToOrganizationTeamMappings(this.teams, this.organizationTeamMappings);
          this.getOrganizationTeamMappings();
          this.indicators.isMappingRunning = false;
        }
      })
      .catch((err) => {
        this.uiErrorHandlingService.handleModal(err);
        this.indicators.loading = false;
        this.indicators.isMappingRunning = false;
      });
  }

  private checkIfMappingIsDone = (): void => {
    this.workdayUserProvisioningService
      .getMappingRun(this.currentMappingId)
      .then((mapping) => {
        if (mapping?.status === WorkdayMappingRunStatus.Ended) {
          this.workdayUserProvisioningService.mapTeamsToOrganizationTeamMappings(this.teams, this.organizationTeamMappings);
          this.getOrganizationTeamMappings();
          this.clearIntervalValue();
        } else if (mapping?.status === WorkdayMappingRunStatus.Failed) {
          this.clearIntervalValue();
          this.indicators.mappingFailed = true;
          this.indicators.loading = false;
        } else {
          if (!this.intervalValue) {
            this.intervalValue = this.$interval(this.checkIfMappingIsDone, pollingInterval);
          }
        }
      })
      .catch((error) => {
        this.clearIntervalValue();
        this.uiErrorHandlingService.handleModal(error);
      });
  };

  private clearIntervalValue(): void {
    this.indicators.isMappingRunning = false;
    if (this.intervalValue) {
      this.$interval.cancel(this.intervalValue);
      delete this.intervalValue;
    }
  }

  downloadReport(): void {
    const organizationTeamMappingsCSV = toCsv(this.organizationTeamMappings, ["gtmhubTeamId", "gtmhubTeamName", "workdayOrganizationId", "workdayOrganizationName"]);
    triggerFileDownload(new Blob([organizationTeamMappingsCSV], { type: "application/octet-stream" }), "organization-team-mapping.csv");

    const workerUserMappingsCSV = toCsv(this.gtmhubUserToWorkdayWorkerMappings, ["gtmhubEmail", "gtmhubId", "gtmhubName", "workdayEmails", "workdayId", "workdayName"]);
    triggerFileDownload(new Blob([workerUserMappingsCSV], { type: "application/octet-stream" }), "worker-user-mapping.csv");
  }

  private getOrganizationTeamMappings = (): void => {
    this.workdayUserProvisioningService.getOrganizationTeamMapping().then(
      (response) => {
        this.workdayUserProvisioningService.mapTeamAndOrganizationNameToId(response.gtmhubToWorkdayMappings, this.organizationTeamMappings, this.organizations);
        this.mappedOrganizationsNo = Object.keys(response.gtmhubToWorkdayMappings).length;
        this.teamsNotMappedNo = Object.keys(this.teams).length - this.mappedOrganizationsNo;
        this.getWorkerUserMappings();
      },
      (err) => {
        this.uiErrorHandlingService.handleModal(err);
        this.indicators.loading = false;
      }
    );
  };

  private getWorkerUserMappings = (): void => {
    this.workdayUserProvisioningService.getWorkerUserMappings().then(
      (response) => {
        this.addIdsToUserWorkerMappings(response.gtmhubToWorkdayMappings);
        this.getWorkdayWorkers();
      },
      (err) => {
        this.uiErrorHandlingService.handleModal(err);
        this.indicators.loading = false;
      }
    );
  };

  private getWorkdayWorkers(): void {
    this.workdayUserProvisioningService.getMappedWorkdayWorkers().then(
      (response) => {
        this.mapWorkerToGtmhubUserToWorkdayWorkerMappings(response);
        this.getGtmhubUsers();
      },
      (err) => {
        this.uiErrorHandlingService.handleModal(err);
        this.indicators.loading = false;
      }
    );
  }

  private getGtmhubUsers(): void {
    this.userService.getUsers().then(
      (response) => {
        this.mapGtmhubUserToGtmhubUserToWorkdayWorkerMappings(response.items);
        this.indicators.loading = false;
      },
      (err) => {
        this.uiErrorHandlingService.handleModal(err);
        this.indicators.loading = false;
      }
    );
  }

  private mapWorkerToGtmhubUserToWorkdayWorkerMappings(workers: IWorkdayWorker[]) {
    this.gtmhubUserToWorkdayWorkerMappings.forEach((gtmhubUserToWorkdayWorkerMapping) => {
      const worker = workers?.find((worker) => worker.id === gtmhubUserToWorkdayWorkerMapping.workdayId);
      if (worker) {
        gtmhubUserToWorkdayWorkerMapping.workdayEmails = worker.email_addresses;
        gtmhubUserToWorkdayWorkerMapping.workdayName = worker.name;
      }
    });
  }

  private mapGtmhubUserToGtmhubUserToWorkdayWorkerMappings(users: IUser[]) {
    this.gtmhubUserToWorkdayWorkerMappings.forEach((gtmhubUserToWorkdayWorkerMapping) => {
      const user = users?.find((user) => user.id === gtmhubUserToWorkdayWorkerMapping.gtmhubId);
      if (user) {
        gtmhubUserToWorkdayWorkerMapping.gtmhubEmail = user.email;
        gtmhubUserToWorkdayWorkerMapping.gtmhubName = user.name;
      }
    });
  }

  private addIdsToUserWorkerMappings(gtmhubToWorkdayMappings: Record<string, string>) {
    this.gtmhubUserToWorkdayWorkerMappings = [];
    for (const userId in gtmhubToWorkdayMappings) {
      this.gtmhubUserToWorkdayWorkerMappings.push({ gtmhubId: userId, workdayId: gtmhubToWorkdayMappings[userId] });
    }
  }
}

export const WizardMappingComponent: IComponentOptions = {
  controller: WizardMappingCtrl,
  templateUrl: require("./mapping.jade"),
  bindings: {
    indicators: "=",
  },
};
